import { useState } from 'react'
import { Box, Dialog, DialogContent } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {
  DateTimePicker,
  LocalizationProvider,
  renderTimeViewClock,
} from '@mui/x-date-pickers'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import CheckIcon from '@mui/icons-material/Check'
import dayjs, { Dayjs } from 'dayjs'
import { isEqual } from 'lodash'
import { FONT_SANS, PillButton, TOKENS, TogSwitch } from './SchedulerDesign'

type FormState = {
  name: string
  time: Dayjs
  editable: boolean
  defaultToMonday: boolean
}

const INITIAL_STATE: FormState = {
  name: '',
  // Set to current week Monday by default.
  time: dayjs().startOf('week').add(1, 'day'),
  editable: true,
  defaultToMonday: true,
}

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      fontSize: 9.5,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: TOKENS.ink400,
      mb: '4px',
      fontFamily: FONT_SANS,
    }}
  >
    {children}
  </Box>
)

const NameInput = ({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) => (
  <Box
    component="input"
    type="text"
    value={value}
    placeholder="e.g. Promo week reschedule"
    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
    sx={{
      width: '100%',
      height: 32,
      border: `1px solid ${TOKENS.line300}`,
      borderRadius: '2px',
      bgcolor: '#fff',
      outline: 'none',
      px: '10px',
      fontFamily: FONT_SANS,
      fontSize: 12.5,
      color: TOKENS.ink900,
      boxSizing: 'border-box',
      '&:focus': { borderColor: TOKENS.brandTeal },
    }}
  />
)

type CreateVersionDialogProps = {
  open: boolean
  onClose: () => void
  isCreating: boolean
  onSave: (formData: Record<string, unknown>) => void
}

/**
 * Create-version dialog styled to match the Scheduler design system
 * (TOKENS/PillButton/TogSwitch — see ConfigurationSettingsDialog), not
 * generic MUI. Same fields/shape as the legacy ScheduleVersionModal so it
 * hits the same create-version API unchanged.
 */
export const CreateVersionDialog = ({
  open,
  onClose,
  isCreating,
  onSave,
}: CreateVersionDialogProps) => {
  const [formData, setFormData] = useState<FormState>(INITIAL_STATE)

  const handleClear = () => setFormData(INITIAL_STATE)

  const handleClose = () => {
    setFormData(INITIAL_STATE)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 'min(440px, 94vw)',
          border: `1px solid ${TOKENS.line300}`,
          borderRadius: '3px',
          overflow: 'visible',
          m: '24px',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '11px',
          px: '18px',
          py: '15px',
          borderBottom: `1px solid ${TOKENS.line200}`,
          bgcolor: TOKENS.surface0,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '2px',
            bgcolor: TOKENS.tealTint,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <AddIcon sx={{ fontSize: 18, color: TOKENS.stateComplete }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: TOKENS.ink900,
              letterSpacing: '-0.01em',
              fontFamily: FONT_SANS,
            }}
          >
            Create version
          </Box>
          <Box sx={{ fontSize: 11.5, color: TOKENS.ink400 }}>
            Snapshots the current schedule into a new version.
          </Box>
        </Box>
        <Box
          component="button"
          onClick={handleClose}
          sx={{
            border: 'none',
            bgcolor: 'transparent',
            cursor: 'pointer',
            p: '4px',
            borderRadius: '2px',
            display: 'flex',
            '&:hover': { bgcolor: TOKENS.surface50 },
          }}
        >
          <CloseIcon sx={{ fontSize: 18, color: TOKENS.ink400 }} />
        </Box>
      </Box>

      {/* Body */}
      <DialogContent sx={{ p: '16px 18px', bgcolor: TOKENS.surface50 }}>
        <Box sx={{ mb: '4px' }}>
          <FieldLabel>Version name</FieldLabel>
          <NameInput
            value={formData.name}
            onChange={(v) => setFormData((p) => ({ ...p, name: v }))}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '11px',
            py: '11px',
            borderTop: `1px solid ${TOKENS.line100}`,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                fontSize: 12.5,
                fontWeight: 600,
                color: TOKENS.ink900,
                fontFamily: FONT_SANS,
              }}
            >
              Default to Monday
            </Box>
            <Box sx={{ fontSize: 10.5, color: TOKENS.ink400, mt: '1px' }}>
              Off for a mid-week reschedule — lets you pick the exact start
              time below.
            </Box>
          </Box>
          <TogSwitch
            on={formData.defaultToMonday}
            onChange={(v) =>
              setFormData((p) => ({
                ...p,
                defaultToMonday: v,
                // Toggling on snaps back to Monday 00:00. Toggling off
                // preselects "now" so a mid-week reschedule defaults to the
                // current hour-of-week without forcing a manual pick.
                time: v ? dayjs().startOf('week').add(1, 'day') : dayjs(),
              }))
            }
          />
        </Box>

        {!formData.defaultToMonday && (
          <Box sx={{ py: '11px', borderTop: `1px solid ${TOKENS.line100}` }}>
            <FieldLabel>Version time</FieldLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '2px',
                    fontFamily: FONT_SANS,
                    fontSize: 12.5,
                  },
                }}
                value={formData.time}
                onChange={(date: Dayjs | null) => {
                  if (date) setFormData((p) => ({ ...p, time: date }))
                }}
                viewRenderers={{
                  hours: renderTimeViewClock,
                  minutes: renderTimeViewClock,
                  seconds: renderTimeViewClock,
                }}
              />
            </LocalizationProvider>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: '11px',
            borderTop: `1px solid ${TOKENS.line100}`,
          }}
        >
          <Box
            sx={{
              fontSize: 12.5,
              fontWeight: 600,
              color: TOKENS.ink900,
              fontFamily: FONT_SANS,
            }}
          >
            Editable
          </Box>
          <TogSwitch
            on={formData.editable}
            onChange={(v) => setFormData((p) => ({ ...p, editable: v }))}
          />
        </Box>
      </DialogContent>

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          px: '18px',
          py: '12px',
          borderTop: `1px solid ${TOKENS.line200}`,
          bgcolor: TOKENS.surface0,
        }}
      >
        <PillButton
          kind="ghost"
          icon={<RestartAltIcon />}
          onClick={handleClear}
          disabled={isEqual(INITIAL_STATE, formData)}
        >
          Clear
        </PillButton>
        <Box sx={{ flex: 1 }} />
        <PillButton
          kind="primary"
          icon={<CheckIcon />}
          disabled={isEqual(INITIAL_STATE, formData) || isCreating}
          onClick={() =>
            onSave({
              ...formData,
              time: formData.time.toISOString(),
            })
          }
        >
          {isCreating ? 'Saving…' : 'Save'}
        </PillButton>
      </Box>
    </Dialog>
  )
}
